import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { analyticsAPI } from '../api';

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

const Sparkline = memo(function Sparkline({
  data,
  dataKey,
  color = '#60a5fa',
  width = 120,
  height = 32,
}) {
  if (!data || data.length < 2) return null;
  const values = data.map((d) => d[dataKey]);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatNumber(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(n);
}

function formatDelta(pct) {
  if (pct == null || !Number.isFinite(pct)) return null;
  const rounded = Math.round(pct * 10) / 10;
  if (rounded === 0) return { label: '±0%', tone: 'neutral' };
  const sign = rounded > 0 ? '+' : '';
  const tone = rounded > 0 ? 'up' : 'down';
  return { label: `${sign}${rounded}%`, tone };
}

function DeltaBadge({ pct }) {
  const d = formatDelta(pct);
  if (!d) return null;
  return <span className={`delta-badge delta-${d.tone}`}>{d.label}</span>;
}

// ISO-YYYY-MM-DD for a Date, using UTC (matches GA4 server time best enough
// for range pickers — the user's timezone mostly shouldn't flip a day).
function toIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

function daysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return toIsoDate(d);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function Analytics() {
  // Range state — period is the preset label; customStart/End are only set
  // when the user switches to "Custom" range.
  const [period, setPeriod] = useState('7d');
  const [customStart, setCustomStart] = useState(daysAgo(7));
  const [customEnd, setCustomEnd] = useState(daysAgo(0));
  const [useCustom, setUseCustom] = useState(false);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedProperty, setExpandedProperty] = useState(null);
  const [detailReport, setDetailReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Effective range values for all API calls — derived during render rather
  // than stored in state (rerender-derived-state-no-effect). API client
  // treats `start`+`end` as the custom range signal; `period` is ignored
  // when both are present, so we pass `undefined` for `period` in custom mode.
  const effectiveStart = useCustom ? customStart : undefined;
  const effectiveEnd = useCustom ? customEnd : undefined;

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsAPI.getOverview(
        useCustom ? undefined : period,
        effectiveStart,
        effectiveEnd,
      );
      setOverview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
    // Primitive deps — not the derived object, which would retrigger on every
    // render (rerender-dependencies).
  }, [period, useCustom, effectiveStart, effectiveEnd]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleCardClick = async (propertyId, configured) => {
    if (!configured) return;
    if (expandedProperty === propertyId) {
      setExpandedProperty(null);
      setDetailReport(null);
      return;
    }
    setExpandedProperty(propertyId);
    setDetailLoading(true);
    try {
      const res = await analyticsAPI.getReport(
        propertyId,
        useCustom ? undefined : period,
        effectiveStart,
        effectiveEnd,
      );
      setDetailReport(res.data);
    } catch {
      setDetailReport(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const selectPreset = (p) => {
    setPeriod(p);
    setUseCustom(false);
    setExpandedProperty(null);
    setDetailReport(null);
  };

  const toggleCustom = () => {
    setUseCustom(true);
    setExpandedProperty(null);
    setDetailReport(null);
  };

  // Aggregate totals card — summed across all *configured* properties that
  // fetched successfully. Derived during render, memoized on the report list.
  const totals = useMemo(() => {
    if (!overview?.reports) return null;
    const base = {
      activeUsers: 0,
      sessions: 0,
      pageViews: 0,
      prevActiveUsers: 0,
      prevSessions: 0,
      prevPageViews: 0,
      configuredCount: 0,
    };
    for (const r of overview.reports) {
      if (!r.configured || !r.metrics) continue;
      base.activeUsers += r.metrics.activeUsers || 0;
      base.sessions += r.metrics.sessions || 0;
      base.pageViews += r.metrics.pageViews || 0;
      base.prevActiveUsers += r.previous?.activeUsers || 0;
      base.prevSessions += r.previous?.sessions || 0;
      base.prevPageViews += r.previous?.pageViews || 0;
      base.configuredCount += 1;
    }
    const pctChange = (cur, prev) => (prev ? ((cur - prev) / prev) * 100 : (cur > 0 ? 100 : 0));
    return {
      ...base,
      deltaActiveUsers: pctChange(base.activeUsers, base.prevActiveUsers),
      deltaSessions: pctChange(base.sessions, base.prevSessions),
      deltaPageViews: pctChange(base.pageViews, base.prevPageViews),
    };
  }, [overview]);

  const exportUrl = (kind) =>
    analyticsAPI.getExportUrl(kind, useCustom ? undefined : period, effectiveStart, effectiveEnd);

  return (
    <div className="component-container">
      <div className="component-header">
        <h2>Google Analytics</h2>
        <div className="header-actions">
          <div className="period-selector">
            {['7d', '30d', '90d'].map((p) => (
              <button
                key={p}
                type="button"
                className={`period-btn ${!useCustom && period === p ? 'active' : ''}`}
                onClick={() => selectPreset(p)}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              className={`period-btn ${useCustom ? 'active' : ''}`}
              onClick={toggleCustom}
            >
              Custom
            </button>
          </div>
          {useCustom ? (
            <div className="custom-range-picker">
              <input
                type="date"
                value={customStart}
                max={customEnd}
                onChange={(e) => setCustomStart(e.target.value)}
                className="input-small"
                aria-label="Start date"
              />
              <span className="range-sep">→</span>
              <input
                type="date"
                value={customEnd}
                min={customStart}
                max={toIsoDate(new Date())}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="input-small"
                aria-label="End date"
              />
            </div>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="analytics-loading">
          <p>Loading analytics data...</p>
        </div>
      ) : null}

      {error ? (
        <div className="analytics-error">
          <p className="error-text">{error}</p>
        </div>
      ) : null}

      {!loading && !error && overview ? (
        <>
          {totals && totals.configuredCount > 0 ? (
            <div className="analytics-totals-card">
              <div className="totals-header">
                <h3>All Sites</h3>
                <span className="totals-subtitle">
                  {totals.configuredCount} configured propert{totals.configuredCount === 1 ? 'y' : 'ies'}
                </span>
              </div>
              <div className="totals-grid">
                <div className="totals-metric">
                  <span className="totals-value">{formatNumber(totals.activeUsers)}</span>
                  <span className="totals-label">Users</span>
                  <DeltaBadge pct={totals.deltaActiveUsers} />
                </div>
                <div className="totals-metric">
                  <span className="totals-value">{formatNumber(totals.sessions)}</span>
                  <span className="totals-label">Sessions</span>
                  <DeltaBadge pct={totals.deltaSessions} />
                </div>
                <div className="totals-metric">
                  <span className="totals-value">{formatNumber(totals.pageViews)}</span>
                  <span className="totals-label">Page Views</span>
                  <DeltaBadge pct={totals.deltaPageViews} />
                </div>
              </div>
              <div className="totals-exports">
                <a className="btn-export" href={exportUrl('overview')}>Export Overview</a>
                <a className="btn-export" href={exportUrl('daily')}>Export Daily</a>
                <a className="btn-export" href={exportUrl('pages')}>Export Pages</a>
              </div>
            </div>
          ) : null}

          <div className="analytics-grid">
            {overview.reports.map((report) => {
              const expanded = expandedProperty === report.propertyId;
              if (!report.configured) {
                return (
                  <div key={report.domain} className="analytics-card not-configured">
                    <div className="analytics-card-header">
                      <h4>{report.domain}</h4>
                    </div>
                    <p className="not-configured-text">GA property not linked</p>
                  </div>
                );
              }
              return (
                <div
                  key={report.propertyId || report.domain}
                  className={`analytics-card ${expanded ? 'expanded' : ''} ${report.error ? 'has-error' : ''}`}
                  onClick={() => handleCardClick(report.propertyId, report.configured)}
                >
                  <div className="analytics-card-header">
                    <h4>{report.domain}</h4>
                  </div>

                  {report.error ? (
                    <p className="error-text" style={{ fontSize: '0.8rem' }}>{report.error}</p>
                  ) : report.metrics ? (
                    <div className="analytics-metrics">
                      <div className="analytics-metric">
                        <span className="metric-value">{formatNumber(report.metrics.activeUsers)}</span>
                        <span className="metric-label">Users</span>
                        <DeltaBadge pct={report.delta?.activeUsers} />
                      </div>
                      <div className="analytics-metric">
                        <span className="metric-value">{formatNumber(report.metrics.sessions)}</span>
                        <span className="metric-label">Sessions</span>
                        <DeltaBadge pct={report.delta?.sessions} />
                      </div>
                      <div className="analytics-metric">
                        <span className="metric-value">{formatNumber(report.metrics.pageViews)}</span>
                        <span className="metric-label">Views</span>
                        <DeltaBadge pct={report.delta?.pageViews} />
                      </div>
                    </div>
                  ) : null}

                  {expanded ? (
                    <div className="analytics-detail" onClick={(e) => e.stopPropagation()}>
                      {detailLoading ? (
                        <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Loading details...</p>
                      ) : detailReport ? (
                        <DetailView
                          detail={detailReport}
                          metrics={report.metrics}
                        />
                      ) : (
                        <p className="error-text" style={{ fontSize: '0.8rem' }}>Failed to load details</p>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      <div className="component-footer">
        <p>GA4 data via service account. Click a card for details.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail view (extracted so the parent doesn't re-render on drill-down)
// ---------------------------------------------------------------------------

function DetailView({ detail, metrics }) {
  const devices = detail.devices || [];
  const totalDeviceUsers = devices.reduce((s, d) => s + (d.users || 0), 0) || 1;

  return (
    <>
      <div className="detail-row">
        <span className="detail-label">Avg. Session</span>
        <span className="detail-value">{formatDuration(metrics?.avgSessionDuration || 0)}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Bounce Rate</span>
        <span className="detail-value">{((metrics?.bounceRate || 0) * 100).toFixed(1)}%</span>
      </div>

      {detail.daily && detail.daily.length > 1 ? (
        <div className="detail-sparklines">
          <div>
            <span className="metric-label">Sessions</span>
            <Sparkline data={detail.daily} dataKey="sessions" color="#ea580c" width={220} height={44} />
          </div>
          <div>
            <span className="metric-label">Page Views</span>
            <Sparkline data={detail.daily} dataKey="pageViews" color="#10b981" width={220} height={44} />
          </div>
        </div>
      ) : null}

      {detail.topPages && detail.topPages.length > 0 ? (
        <div className="detail-section">
          <h5>Top Pages</h5>
          <ul className="detail-list">
            {detail.topPages.slice(0, 5).map((page) => (
              <li key={page.path}>
                <span className="detail-list-key" title={page.path}>{page.path}</span>
                <span className="detail-list-value">{formatNumber(page.views)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {detail.topCountries && detail.topCountries.length > 0 ? (
        <div className="detail-section">
          <h5>Top Countries</h5>
          <ul className="detail-list">
            {detail.topCountries.slice(0, 5).map((c) => (
              <li key={c.country}>
                <span className="detail-list-key">{c.country}</span>
                <span className="detail-list-value">{formatNumber(c.users)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {devices.length > 0 ? (
        <div className="detail-section">
          <h5>Devices</h5>
          <div className="device-bar">
            {devices.map((d) => {
              const pct = ((d.users || 0) / totalDeviceUsers) * 100;
              return (
                <div
                  key={d.category}
                  className={`device-segment device-${d.category}`}
                  style={{ width: `${pct}%` }}
                  title={`${d.category}: ${formatNumber(d.users)} users (${pct.toFixed(0)}%)`}
                >
                  {pct > 10 ? d.category : ''}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {detail.topSources && detail.topSources.length > 0 ? (
        <div className="detail-section">
          <h5>Top Sources</h5>
          <ul className="detail-list">
            {detail.topSources.slice(0, 5).map((s) => (
              <li key={s.source}>
                <span className="detail-list-key">{s.source}</span>
                <span className="detail-list-value">{formatNumber(s.sessions)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

export default Analytics;
